import { STAGE_CHANNEL, type PresentMessage } from "@msc/shared";

/**
 * Typisierter Wrapper um den BroadcastChannel, über den Cockpit (State-Owner)
 * und Bühnenfenster (dummer Renderer) synchron bleiben. Same-origin, gleicher
 * Browser — genau das Zwei-Fenster-Szenario des Präsentationsmodus.
 *
 * **Der Kanal ist je Origin, nicht je Präsentation.** Jeder offene CRM-Tab
 * hört mit, und ein zweiter Tab mit einer wiederhergestellten Präsentation
 * (Reload, duplizierter Tab, ein vergessener Tab von gestern) beantwortete
 * `state-request` mit SEINEM Stand — das Kundenfenster zeigte ein Ticket statt
 * der Agenda (2026-09-23). Deshalb trägt jede Nachricht den `owner` (die
 * `ownerId` der Präsentation), `post` stempelt ihn, und `onMessage` sieht nur
 * Nachrichten desselben Owners. Ein Fenster OHNE Owner (Adresse ohne
 * `?owner=`, etwa der E2E-Test) nimmt alles an — so wie vor dieser Regel.
 *
 * Port aus der Hub-App (`jira-customer-view/frontend/src/lib/presentChannel.ts`);
 * Abweichungen: Kanalname `STAGE_CHANNEL` (`@msc/shared`) statt
 * `jcv-presentation`, und der Owner-Filter.
 */
/** `Omit` über eine Union muss VERTEILT werden, sonst bleiben nur die gemeinsamen Felder. */
type OhneOwner<M> = M extends unknown ? Omit<M, "owner"> : never;

export interface PresentChannel {
  post: (m: OhneOwner<PresentMessage>) => void;
  close: () => void;
}

/** Nimmt dieser Teilnehmer die Nachricht an? Reine Regel, für den Test. */
export function acceptsMessage(owner: string, data: unknown): data is PresentMessage {
  if (!data || typeof data !== "object") return false;
  const m = data as { type?: unknown; owner?: unknown };
  if (typeof m.type !== "string") return false;
  if (owner === "") return true;
  return m.owner === owner;
}

export function openPresentChannel(
  owner: string,
  onMessage: (m: PresentMessage) => void,
): PresentChannel {
  const ch = new BroadcastChannel(STAGE_CHANNEL);
  ch.onmessage = (ev: MessageEvent<unknown>) => {
    if (acceptsMessage(owner, ev.data)) onMessage(ev.data);
  };
  return {
    post: (m) => ch.postMessage({ ...m, owner } as PresentMessage),
    close: () => ch.close(),
  };
}
